import React from "react";
import { twMerge } from "tailwind-merge";
import Label from "@/components/form-controls/Label";

type INumberInputProps = React.ComponentProps<"input"> & {
    label: string;
    placeholder?: string;
    onBlur?: () => void;
    error?: string;
    register?: any;
    labelStyle?: string;
    inputStyle?: string;
    hasError?: boolean;
    hint?: string;
    prefix?: React.ReactNode;
    onChange?: (value: number) => void;
}

export default function NumberInput({
    label,
    placeholder,
    onBlur,
    error,
    register,
    hasError,
    className,
    inputStyle,
    labelStyle,
    hint,
    prefix,
    onChange,
    ...extra
}: INumberInputProps) {
    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleIncrement = () => {
        if (!inputRef.current) return;
        
        const currentValue = inputRef.current.value === '' ? 0 : parseFloat(inputRef.current.value);
        const newValue = currentValue + 1;
        
        const event = new Event('input', { bubbles: true });
        inputRef.current.value = newValue.toString();
        inputRef.current.dispatchEvent(event);
        
        register.onChange({
            target: {
                name: register.name,
                value: newValue
            }
        });
    };

    const handleDecrement = () => {
        if (!inputRef.current) return;
        
        const currentValue = inputRef.current.value === '' ? 0 : parseFloat(inputRef.current.value);
        const newValue = Math.max(0, currentValue - 1);
        
        const event = new Event('input', { bubbles: true });
        inputRef.current.value = newValue.toString();
        inputRef.current.dispatchEvent(event);
        
        register.onChange({
            target: {
                name: register.name,
                value: newValue
            }
        });
    };

    return (
        <div className={className}>
            <Label 
                name={extra.name ?? ""} 
                text={label} 
                required={extra.required}
                className={labelStyle}
                hint={hint}
            />
            <div>
                <input
                    placeholder={placeholder}
                    className={twMerge(
                        "input", 
                        hasError ? "input-error" : "input-default font-extrabold",
                        prefix ? "pl-12" : "",
                        (extra.disabled || extra.readOnly) && "bg-primary-100 text-black",
                        inputStyle
                    )}
                    {...register}
                    ref={(e) => {
                        register.ref(e);
                        // @ts-ignore
                        inputRef.current = e;
                    }}
                    onBlur={(e) => {
                        register?.onBlur?.(e);
                        onBlur?.();
                    }}
                    min="0"
                    step="any"
                    {...extra}
                />
            </div>

            <div className="absolute right-1 top-6">
                <div className="flex justify-center items-center">
                    <button
                        type="button"
                        onClick={handleDecrement}
                        className="px-2 text-[30px] font-light text-[#6B5C4F]  rounded"
                    >
                        -
                    </button>
                    <button
                        type="button"
                        onClick={handleIncrement}
                        className="px-2 text-[30px] font-light text-[#6B5C4F]  rounded"
                    >
                        +
                    </button>
                </div>
            </div>
  
            {error && <p className="mt-1 text-xs text-rose-800 border-rose-800">{error}</p>}
        </div>
    );
}
  